import type { InboxEntry, InboxMode } from './inboxApi'
import type { InboxPage } from './inboxApi'
import type { InfiniteData, QueryClient } from '@tanstack/react-query'
import { compareIds } from './messageMerge'
import { channelMuted, DM_SETTINGS, ownMuted } from './muting'
import { channelPermissions, has, P, threadPermissions } from './permissions'
import { ChannelType, type Channel, type Guild, type Member, type ReadState, type Relationship, type UserGuildSettings } from '@/types/api'

interface InboxContext {
  userId: string
  ready: boolean
  mode: InboxMode
  unreadOnly: boolean
  includeMuted: boolean
  guilds: Record<string, Guild>
  channels: Record<string, Record<string, Channel>>
  members: Record<string, Record<string, Member>>
  settings: Record<string, UserGuildSettings>
  privateChannels: Record<string, Channel>
  readStates: Record<string, ReadState>
  relationships: Record<string, Relationship>
}

/** Сервер проверяет историю, клиент сразу скрывает карточку при изменении доступа/прочтения. */
export function inboxEntryVisible(entry: InboxEntry, ctx: InboxContext): boolean {
  const ch = entry.channel
  const gid = ch.guild_id
  if (entry.messages.every((m) => ctx.relationships[m.author.id]?.type === 2)) return false
  const boundary = ctx.mode === 'mentions' ? entry.messages[0]?.id : entry.last_message_id
  if ((ctx.mode === 'unread' || ctx.unreadOnly) && boundary && readThrough(ctx.readStates[ch.id], boundary)) return false
  if (gid) {
    const guild = ctx.guilds[gid]
    if (ctx.ready && !guild) return false
    const live = ctx.channels[gid]?.[ch.id]
    const parent = ch.parent_id ? ctx.channels[gid]?.[ch.parent_id] : undefined
    if (ctx.ready && !live && (ch.type !== ChannelType.PublicThread || !parent)) return false
    if (ctx.mode === 'unread' && parent?.type === ChannelType.GuildForum && !(live ?? ch).member && !(ctx.readStates[ch.id]?.mention_count ?? 0)) return false
    if (guild && (live || parent)) {
      const self = ctx.members[gid]?.[ctx.userId] ?? { user: { id: ctx.userId }, roles: [] }
      const perms = ch.type === ChannelType.PublicThread ? threadPermissions(guild, self, parent) : channelPermissions(guild, self, live)
      if (!has(perms, P.VIEW_CHANNEL) || !has(perms, P.READ_MESSAGE_HISTORY)) return false
    }
    if (!ctx.includeMuted && channelMuted(ctx.settings[gid], ch.id, { ...ctx.channels[gid], [ch.id]: live ?? ch })) return false
  } else {
    if (ctx.ready && !ctx.privateChannels[ch.id]) return false
    if (!ctx.includeMuted && ownMuted(ctx.settings[DM_SETTINGS], ch.id)) return false
  }
  return true
}

export function readThrough(state: ReadState | undefined, messageId: string): boolean {
  return Boolean(state?.last_message_id && compareIds(state.last_message_id, messageId) >= 0)
}

/** ACK со старого устройства не передвигает границу назад. */
export function mergeAck(current: ReadState | undefined, incoming: ReadState): ReadState {
  if (current?.last_message_id && incoming.last_message_id && compareIds(current.last_message_id, incoming.last_message_id) > 0) return current
  return incoming
}

/** Удаление скрывает старое превью даже без открытого исходного канала. */
export function applyInboxDeletion(client: QueryClient, userId: string | undefined, t: string, data: unknown) {
  if (!userId || !['MESSAGE_DELETE', 'MESSAGE_DELETE_BULK', 'THREAD_DELETE', 'CHANNEL_DELETE'].includes(t)) return
  const d = data as { channel_id: string; id?: string; ids?: string[] }
  const removed = new Set(t === 'MESSAGE_DELETE_BULK' ? d.ids : [d.id])
  const prefix = ['inbox', userId]
  void client.cancelQueries({ queryKey: prefix })
  client.setQueriesData<InfiniteData<InboxPage>>({ queryKey: prefix }, (old) => old && ({ ...old, pages: old.pages.map((page) => ({ ...page, items: page.items.flatMap((entry) => {
    if ((t === 'THREAD_DELETE' || t === 'CHANNEL_DELETE') && entry.channel.id === d.id) return []
    if (entry.channel.id !== d.channel_id) return [entry]
    const messages = entry.messages.filter((m) => !removed.has(m.id))
    return messages.length ? [{ ...entry, messages }] : []
  }) })) }))
  void client.invalidateQueries({ queryKey: prefix })
}
